// TomorrowPreviewStep.tsx - 重構版本
import React, { useState, useEffect, useCallback } from 'react';
import { DayCloseStepProps } from '../../types/dayClose';
import { ScheduleItem, isTimeBox, isEvent, getScheduleItemStartTime, getScheduleItemEndTime, sortScheduleItems } from '../../types/schedule';
import { useDayCloseAPI } from '../../hooks/useDayCloseAPI';
import { useModal } from '../../contexts/ModalContext';
import { Calendar, Clock, Crosshair, Users, Coffee, Sparkles } from 'lucide-react';
import { useScheduleGeneratorTrigger } from '../../features/scheduleGenerator';
import { formatTime } from '../../utils/formatters';
import { formatDateToString } from '../../utils/dateUtils';
import { useToast } from '../../contexts/ToastContext';
import { logger } from '../../utils/logger';

const TomorrowPreviewStep: React.FC<DayCloseStepProps> = ({
  dayCloseId,
  onComplete,
  userId,
  setLoading,
  setCanContinue,
  setStepHandlers
}) => {
  const { showError } = useToast();
  const { finishDayClose, loadTomorrowScheduleItems } = useDayCloseAPI();
  const { pauseDayCloseModal, resumeDayCloseModal } = useModal();
  const { triggerAIPlan } = useScheduleGeneratorTrigger();
  const [loading, setLoadingState] = useState(true);
  const [scheduleItems, setScheduleItems] = useState<ScheduleItem[]>([]);
  const [isCompleting, setIsCompleting] = useState(false);


  // 計算統計數據
  const calculateStats = () => {
    if (scheduleItems.length === 0) return null;

    // 分離 TimeBoxes 和 Events
    const timeBoxes = scheduleItems.filter(isTimeBox);
    const events = scheduleItems.filter(isEvent);

    // 區分全天事件與有時段項目（全天事件排除在統計之外）
    // 重新排序確保 firstStart/lastEnd 取到正確的最早/最晚時間
    const timedItems = sortScheduleItems([
      ...timeBoxes,
      ...events.filter(e => e.timeType !== 'allday'),
    ]);

    // 只計算有時段項目的總時間（全天事件排除在外）
    const totalHours = timedItems.reduce((total, item) => {
      const startTime = getScheduleItemStartTime(item);
      const endTime = getScheduleItemEndTime(item);
      const duration = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60);
      return total + duration;
    }, 0);

    const focusHours = timeBoxes
      .filter(box => box.category === 'focus')
      .reduce((total, box) => {
        const duration = (new Date(box.endTime).getTime() - new Date(box.startTime).getTime()) / (1000 * 60 * 60);
        return total + duration;
      }, 0);

    const lifeCount = timeBoxes.filter(box => box.category === 'life').length;
    const restCount = timeBoxes.filter(box => box.category === 'rest').length;
    const eventCount = events.length;

    // 只從有時段項目取最早開始時間（全天事件的 00:00 不算）
    const firstStart = timedItems.length > 0 ? getScheduleItemStartTime(timedItems[0]) : null;
    const lastEnd = timedItems.length > 0 ? getScheduleItemEndTime(timedItems[timedItems.length - 1]) : null;

    return {
      totalScheduleItems: scheduleItems.length,
      totalTimeBoxes: timeBoxes.length,
      totalEvents: eventCount,
      totalHours: totalHours.toFixed(1),
      focusHours: focusHours.toFixed(1),
      lifeCount,
      restCount,
      firstEventTime: firstStart,
      lastEventTime: lastEnd,
      timedItemsCount: timedItems.length,
    };
  };

  // 加載明天的排程項目
  const loadTomorrowItems = useCallback(async () => {
    try {
      setLoadingState(true);
      setLoading?.(true);

      const items = await loadTomorrowScheduleItems(userId);
      setScheduleItems(items);
    } catch (error) {
      logger.error('TomorrowPreviewStep', 'Error loading tomorrow schedule items', error);
    } finally {
      setLoadingState(false);
      setLoading?.(false);
    }
  }, [userId, setLoading, loadTomorrowScheduleItems]);

  useEffect(() => {
    loadTomorrowItems();
  }, [loadTomorrowItems]);

  useEffect(() => {
    setCanContinue?.(true);
  }, [setCanContinue]);

  const handleComplete = useCallback(async () => {
    try {
      setIsCompleting(true);
      setLoading?.(true);

      logger.info('TomorrowPreviewStep', '執行 Day Close 完成操作');
      await finishDayClose(dayCloseId, 'completed');

      logger.info('TomorrowPreviewStep', 'Day Close 完成，移動到最終步驟');
      onComplete(); // 這會觸發 moveToNextStep
    } catch (error) {
      logger.error('TomorrowPreviewStep', '完成 Day Close 失敗', error);
      showError('Day Close Failed', 'Failed to complete day close. Please try again later.');
    } finally {
      setIsCompleting(false);
      setLoading?.(false);
    }
  }, [dayCloseId, finishDayClose, onComplete, setLoading, showError]);

  // 註冊步驟處理函數到 Modal
  useEffect(() => {
    if (setStepHandlers) {
      setStepHandlers({
        handlePreview: handleComplete
      });
    }

    return () => {
      if (setStepHandlers) {
        setStepHandlers({});
      }
    };
  }, [handleComplete, setStepHandlers]);

  if (loading) {
    return (
      <div className="p-6 flex flex-col items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin"></div>
        <p className="mt-4 text-gray-600">Loading tomorrow's schedule...</p>
      </div>
    );
  }

  const stats = calculateStats();

  return (
    <div className="p-6">
      {/* Tomorrow's Overview 統計卡片 */}
      {stats && (
        <div className="mb-6 p-6 bg-gradient-to-r from-sky-50 to-sky-50 rounded-2xl shadow-md">
          <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-center">
            <Calendar className="w-5 h-5 mr-2" />
            Tomorrow's Overview
          </h3>

          <div className="grid grid-cols-2 gap-4">
            {/* 總排程項目數 */}
            <div className="bg-white/70 backdrop-blur rounded-xl p-4">
              <div className="text-3xl font-bold text-sky-600">{stats.totalScheduleItems}</div>
              <div className="text-sm text-gray-600 mt-1">Schedule items</div>
              <div className="text-xs text-gray-500 mt-1">
                {stats.totalTimeBoxes} boxes • {stats.totalEvents} events
              </div>
            </div>

            {/* 總計劃時間：只有全天事件時隱藏，避免顯示 0.0h 誤導用戶 */}
            {stats.timedItemsCount > 0 && (
              <div className="bg-white/70 backdrop-blur rounded-xl p-4">
                <div className="text-3xl font-bold text-sky-600">{stats.totalHours}h</div>
                <div className="text-sm text-gray-600 mt-1">Total scheduled</div>
              </div>
            )}

            {/* 第一個事件時間 */}
            {stats.firstEventTime && (
              <div className="bg-white/70 backdrop-blur rounded-xl p-4">
                <div className="flex items-center">
                  <Clock className="w-5 h-5 text-gray-500 mr-2" />
                  <span className="text-xl font-semibold text-gray-700">
                    {formatTime(stats.firstEventTime)}
                  </span>
                </div>
                <div className="text-sm text-gray-600 mt-1">First event</div>
              </div>
            )}

            {/* 專注時間 */}
            {stats.focusHours !== "0.0" && (
              <div className="bg-white/70 backdrop-blur rounded-xl p-4">
                <div className="flex items-center">
                  <Crosshair className="w-5 h-5 text-sky-500 mr-2" />
                  <span className="text-xl font-semibold text-gray-700">{stats.focusHours}h</span>
                </div>
                <div className="text-sm text-gray-600 mt-1">Focus time</div>
              </div>
            )}

            {/* 生活活動數量 */}
            {stats.lifeCount > 0 && (
              <div className="bg-white/70 backdrop-blur rounded-xl p-4">
                <div className="flex items-center">
                  <Users className="w-5 h-5 text-green-500 mr-2" />
                  <span className="text-xl font-semibold text-gray-700">{stats.lifeCount}</span>
                </div>
                <div className="text-sm text-gray-600 mt-1">Life activit{stats.lifeCount > 1 ? 'ies' : 'y'}</div>
              </div>
            )}

            {/* 休息次數 */}
            {stats.restCount > 0 && (
              <div className="bg-white/70 backdrop-blur rounded-xl p-4">
                <div className="flex items-center">
                  <Coffee className="w-5 h-5 text-amber-500 mr-2" />
                  <span className="text-xl font-semibold text-gray-700">{stats.restCount}</span>
                </div>
                <div className="text-sm text-gray-600 mt-1">Rest break{stats.restCount > 1 ? 's' : ''}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tomorrow's Schedule 時間軸 */}
      <div className="mb-6">
        <h4 className="text-md font-medium mb-3">Tomorrow's Schedule</h4>

        {scheduleItems.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-gray-600">No schedule items for tomorrow</p>
          </div>
        ) : (
          <div className="relative">
            {/* 時間軸主線 */}
            <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-sky-500 to-sky-500"></div>
            
            {/* 排程項目卡片 */}
            <div className="space-y-4">
              {scheduleItems.map((item) => (
                <div key={item.id} className="relative">
                  {/* 時間軸節點 */}
                  <div className="absolute left-4 top-4 w-4 h-4 bg-white border-2 border-sky-500 rounded-full shadow-sm z-10"></div>
                  
                  {/* 項目卡片 */}
                  <div className="ml-12 p-4 bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm font-medium text-gray-500">
                            {isEvent(item) && item.timeType === 'allday'
                              ? 'All day'
                              : `${formatTime(getScheduleItemStartTime(item))} - ${formatTime(getScheduleItemEndTime(item))}`
                            }
                          </span>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            isTimeBox(item)
                              ? 'bg-sky-100 text-sky-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {isTimeBox(item) ? 'TimeBox' : isEvent(item) && item.timeType === 'allday' ? 'All day' : 'Event'}
                          </span>
                        </div>
                        <h4 className="font-medium text-gray-900 mb-1">{item.title}</h4>
                        {(item.notes || item.description) && (
                          <p className="text-sm text-gray-600">{item.notes || item.description}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* AI Plan 按鈕 - 置中並突出 */}
        <div className="mt-6 text-center">
          <button
            onClick={() => {
              // 1. 先暫停 Day Close Modal
              pauseDayCloseModal();

              // 2. 觸發 AI Plan
              const tomorrow = new Date();
              tomorrow.setDate(tomorrow.getDate() + 1);
              triggerAIPlan(
                {
                  source: 'today-tomorrow',
                  date: formatDateToString(tomorrow)
                },
                {
                  onClose: resumeDayCloseModal,
                  onDataRefresh: loadTomorrowItems,
                }
              );
            }}
            className="inline-flex items-center px-6 py-3 bg-sky-500 hover:bg-sky-600 text-white rounded-lg transition-colors shadow-md"
            disabled={isCompleting}
          >
            <Sparkles className="w-5 h-5 mr-2" />
            {(stats === null || stats.timedItemsCount === 0) ? 'Generate Plan' : 'Regenerate'}
          </button>
        </div>
      </div>

    </div>
  );
};

export default TomorrowPreviewStep;